<template>
  <div id="callRecordList">
    <div class="illustrate-content">
      <div>查詢說明：</div>
      <div>1. 可查詢當日起算一年內的通話紀錄，且最多顯示1萬筆紀錄。</div>
      <div>2. 若可查詢區間超過顯示上限（1萬筆），則可透過匯出報表查看完整通話紀錄。</div>
    </div>
    <FilterFrame
      ref="filter"
      :filter-form="recordFilter"
      :auto-search="true"
      @search="(_, isAutoSearch) => onSearchValue('search', isAutoSearch)"
      @clear="clearSearch()"
      @onSearchValue="onSearchValueChange"
    >
    </FilterFrame>
    <div class="title_bar">
      <div class="left" />
      <div class="right">
        <span>共 {{ listTotalCount }} 筆通話紀錄</span>
        <el-button
          icon="icon-output"
          round
          @click="onSearchValue('export')"
        >
          匯出
        </el-button>
      </div>
    </div>
    <el-table
      class="newslist"
      :data="ListData"
      empty-text="目前無任何通話紀錄"
    >
      <el-table-column
        type="index"
        :index="filterIndex"
        width="100"
      />
      <el-table-column
        prop="CallerDisplayName"
        label="撥出方"
      >
        <template slot-scope="scope">
          <span class="noWordBreak">{{ scope.row.CallerDisplayName }}</span>
          <span style=" transform: translateY(2px);">
            <el-popover
              v-if="scope.row.Callback && scope.row.Callback.Type !== 1"
              placement="bottom-start"
              trigger="click"
              popper-class="dark"
              @show="getTabletNotes(scope.row.Callback.Type, scope.row.CallerDisplayName)"
            >
              <template #reference>
                <span>
                  <i
                    class="icon-info"
                    aria-hidden="true"
                    style="margin-left: 7px;"
                  />
                </span>
              </template>
              <div
                ref="popoverWidthRef"
                class="household-info"
              >
                <p
                  v-for="(item, index) in tabletNotesData"
                  :key="index"
                >
                  {{ item.TabletNote }}
                </p>
              </div>
            </el-popover>
          </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="callTypeLabel"
        :label="$t('Intercom.CallType')"
      >
      </el-table-column>
      <el-table-column
        prop="Status"
        label="撥接狀態"
      >
        <template slot-scope="scope">
          <span
            class="status"
            :class="{'status-danger': scope.row.Status === '未接' || scope.row.Status === '未接(勿擾)'}"
          >
            {{ scope.row.Status }}
          </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="CalleeDisplayName"
        label="接聽方"
      >
        <template slot-scope="scope">
          <span class="noWordBreak">{{ scope.row.CalleeDisplayName }}</span>
          <div
            v-if="scope.row.CallForwarding && scope.row.CallForwarding.Fired"
            class="call-forwarding"
          >
            轉接至住戶{{ forwardingMapping[scope.row.CallForwarding.Type] ? forwardingMapping[scope.row.CallForwarding.Type] : '' }}號碼
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="CreateAt"
        label="時間"
      >
        <template slot-scope="scope">
          <span>{{ scope.row.CreateAt ? `${scope.row.CreateAt.split(':')[0]}:${scope.row.CreateAt.split(':')[1]}` : '' }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="DurationInMilliseconds"
        label="通話時間"
      >
        <template slot-scope="scope">
          <span>{{ filterDuration(scope.row.DurationInMilliseconds) }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="AlarmEvent.Id"
        :label="$t('General.Operation')"
      >
        <template slot-scope="scope">
          <el-button
            v-if="scope.row.CallType === CallTypeEnum.EmergencyCall"
            size="mini"
            plain
            :disabled="!scope.row.AlarmEvent?.Id"
            @click="$openNewPageToPath({
              name:'privateEventDetailById',
              params: {
                id: scope.row.AlarmEvent?.Id
              }
            })"
          >
            {{ $t('Intercom.ViewDetails') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <TableLoading :is-loading="loading" />
    <el-pagination
      background
      :current-page.sync="nowPage"
      layout="total, prev, pager, next, jumper"
      :total="listTotalCount"
      @current-change="(page) => getList(page, false, false)"
    >
    </el-pagination>
  </div>
</template>
<script>
import TableLoading from '@/components/TableLoading'
import FilterFrame from '@/components/FilterFrame'

/**
 * 通話類型列舉
 * @readonly
 * @enum {number}
 */
const CallTypeEnum = {
  NormalCall: 1, // 一般通話
  EmergencyCall: 2 // 緊急通話
}
export default {
  components: {
    FilterFrame,
    TableLoading
  },
  data () {
    return {
      filter: {},
      recordFilter: [
        {
          label: '撥打方',
          name: 'caller',
          type: 'input',
          value: ''
        },
        {
          label: '接聽方',
          name: 'receiver',
          type: 'input',
          value: ''
        },
        {
          label: '撥接狀態',
          name: 'status',
          type: 'select',
          options: [
            {
              label: '請選擇',
              value: ''
            },
            {
              label: '未接',
              value: 'NoAnswer'
            },
            {
              label: '接聽',
              value: 'Answered'
            },
            {
              label: '拒接',
              value: 'Decline'
            },
            {
              label: '取消通話',
              value: 'Canceled'
            }
          ],
          value: ''
        },
        {
          label: '時間區間',
          name: 'date',
          type: 'date',
          value: [dayjs().add(-90, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
          pickerOptions: [
            'currentDay',
            'lastSevenDays',
            'lastMonth',
            'lastThreeMonth',
            'lastYear',
            { name: 'disabledDate', value: (time) => { return time.getTime() < dayjs().add(-365, 'day').toDate().getTime() || time.getTime() > dayjs().toDate().getTime() } },
            { name: 'default-value', value: [dayjs().add(-90, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] },
            { name: 'clearable', value: false }
          ]
        },
        {
          label: this.$t('Intercom.CallType'),
          name: 'callType',
          type: 'select',
          options: [
            {
              label: this.$t('General.PleaseSelect'),
              value: null
            },
            {
              label: this.$t('Intercom.NormalCall'),
              value: 1
            },
            {
              label: this.$t('Intercom.EmergencyCall'),
              value: 2
            }
          ],
          value: null
        }
      ],
      activeName: 'callRecordList',
      loading: false,
      maxCount: 10,
      ListData: [],
      listTotalCount: 0,
      nowPage: 1,
      canClickButton: true,
      isValidDate: true,
      forwardingMapping: {
        2: '市話',
        3: '手機'
      },
      tabletNotesData: [], // i被click會顯示戶別
      CallTypeEnum
    }
  },
  mounted () {
    if (!JSON.parse(localStorage.getItem('loginUserInfo')).IsAuthorizationAccount && !JSON.parse(localStorage.getItem('loginUserInfo')).IsFromEip) {
      this.$alert('沒有訪問權限', '警告', {
        confirmButtonText: '確定',
        type: 'warning',
        showClose: false,
        roundButton: true,
        closeOnClickModal: false
      }).then(() => {
        this.$router.back()
      }).catch(() => {})
      return false
    }
  },
  methods: {
    // 查詢
    search (such_val, isAutoSearch) {
      if (this.canClickButton) {
        this.canClickButton = false
        this.ListData = []
        window.scrollTo(0, 0)
        this.getList(1, isAutoSearch)
      }
      setTimeout(() => {
        this.canClickButton = true
      }, 1000)
    },
    // 清除搜尋條件
    clearSearch () {
      if (this.canClickButton) {
        this.canClickButton = false
        this.filter = {
          'CreateAt.GreaterThenOrEqualTo': `${dayjs().add(-90, 'day').format('YYYY-MM-DD')} 00:00:00`,
          'CreateAt.LessThenOrEqualTo': `${dayjs().format('YYYY-MM-DD')} 23:59:59`
        }
        this.ListData = []
        this.isValidDate = true
        this.loading = true
        window.scrollTo(0, 0)
        this.getList(1, true)
      }
      setTimeout(() => {
        this.canClickButton = true
      }, 1000)
    },
    getList (page = 1, isAutoSearch = false, checkLimit = true) {
      this.loading = true
      this.nowPage = page
      this.ListData = []
      apiIntercom.Sentry.GetCallLogs({
        Offset: (page - 1) * 10,
        MaxCount: this.maxCount,
        ...this.filter
      }).then(res => {
        console.log(res)
        this.ListData = res.data.Data.CallLogs
        this.ListData = this.ListData.map(el => {
          let callTypeLabel
          switch (el.CallType) {
          case CallTypeEnum.NormalCall:
            callTypeLabel = this.$t('Intercom.NormalCall')
            break
          case CallTypeEnum.EmergencyCall:
            callTypeLabel = this.$t('Intercom.EmergencyCall')
            break
          default:
            callTypeLabel = this.$t('Intercom.NormalCall')
            break
          }
          return {
            ...el,
            callTypeLabel
          }
        })
        this.listTotalCount = res.data.Data.Total
        this.loading = false
        if (res.data.Data.TotalOverLimit && checkLimit) {
          if (isAutoSearch) {
            const notShowCallRecordOverLimitAlert = localStorage.getItem('notShowCallRecordOverLimitAlert') === 'true'
            if (notShowCallRecordOverLimitAlert) return
            this.$confirm('通話紀錄已達到顯示上限，僅顯示前1萬筆資料。欲檢視完整通話紀錄，請使用「匯出」功能。', '通話紀錄超過顯示上限', {
              confirmButtonText: '確定',
              cancelButtonText: '今日不再顯示',
              type: 'warning'
            }).then(() => {
            }).catch(() => {
              localStorage.setItem('notShowCallRecordOverLimitAlert', true)
            })
          } else {
            this.$alert('查詢結果已達到顯示上限，僅顯示最新的1萬筆資料。欲查看所有結果，請使用「匯出」功能。', '查詢結果已超過顯示上限', {
              confirmButtonText: '確定',
              type: 'warning',
              callback: action => {}
            })
          }
        }
      }).catch(err => {
        console.log(err)
      })
    },
    onSearchValue (type, isAutoSearch = false) {
      this.$refs.filter.onSearchValue()
      if (this.isValidDate) {
        switch (type) {
        case 'search':
          this.search(1, isAutoSearch)
          break
        case 'export':
          this.exportCallLogs()
          break
        }
      }
    },
    onSearchValueChange (such_val) {
      this.filter = {
        CallerDisplayName: such_val.caller ? such_val.caller : undefined,
        CalleeDisplayName: such_val.receiver ? such_val.receiver : undefined,
        Status: such_val.status ? such_val.status : undefined,
        'CreateAt.GreaterThenOrEqualTo': such_val.date ? `${such_val.date[0]} 00:00:00` : undefined,
        'CreateAt.LessThenOrEqualTo': such_val.date ? `${such_val.date[1]} 23:59:59` : undefined,
        CallType: such_val.callType
      }
      const day1 = this.filter['CreateAt.GreaterThenOrEqualTo']
      const day2 = this.filter['CreateAt.LessThenOrEqualTo']
      if (day1 && day2) {
        const dateDiff = dayDiff(day1, day2)
        if (dateDiff > 365) {
          this.$alert('篩選時間範圍上限一年（365天）', '錯誤', {
            confirmButtonText: '確定',
            type: 'error',
            callback: action => {}
          })
          this.isValidDate = false
        } else {
          this.isValidDate = true
        }
      } else {
        this.isValidDate = true
      }
      function dayDiff (startDay, endDay) {
        const startDate = new Date(startDay)
        const endDate = new Date(endDay)
        const diffDays = (endDate - startDate) / 1000 / 60 / 60 / 24
        return diffDays
      }
    },
    exportCallLogs () {
      const config = {
        responseType: 'blob',
        params: this.filter
      }
      apiIntercom.Sentry.GetCallLogsExport(config).then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${decodeURIComponent(res.headers['content-disposition'].split(';')[2].split("=UTF-8''")[1])}`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }).catch(err => {
        console.log(err)
      })
    },
    filterDuration (mss) {
      if (mss === 0) {
        return ''
      }
      const hours = parseInt((mss % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      let minutes = parseInt((mss % (1000 * 60 * 60)) / (1000 * 60))
      let seconds = parseInt((mss % (1000 * 60)) / 1000)
      let returnText = ''
      if (hours > 0) {
        if (minutes > 0 && minutes < 10) {
          minutes = `0${minutes}`
        }
        returnText += `${hours}時`
      }
      if (minutes > 0) {
        if (seconds >= 0 && seconds < 10) {
          seconds = `0${seconds}`
        }
        returnText += `${minutes}分`
      }
      returnText += `${seconds}秒`
      return returnText
    },
    filterIndex (index) {
      return (this.nowPage - 1) * 10 + index + 1
    },
    async getTabletNotes (callForwardingType, callForwardingNumber) {
      try {
        this.tabletNotesData = []
        const res = await apiIntercom.Sentry.GetTabletNotes(callForwardingType, callForwardingNumber)
        this.tabletNotesData = res.data.Data.Tablets
        if (this.tabletNotesData.length === 0) {
          this.tabletNotesData.push({ TabletNote: '查無戶別資料' })
          this.tabletNotesData.push({ TabletNote: '(該號碼已從開通列表中移除)' })
        }
      } catch (err) {
        console.log(err)
      }
    }
  }
}
</script>

<style lang="sass" scoped>
#callRecordList
  text-align: center
  padding: 20px 0 10px
  height: 100%
  .illustrate-content
    text-align: left
    margin-bottom: 20px
    & > div
      line-height: 20px
  .title_bar
    display: flex
    justify-content: space-between
    align-items: center
    margin: 20px 0
    .right button
      margin-left: 16px
  .title_bar.center
    margin: 20px 0 10px 0
  .newslist
    font-size: 15px
    ::v-deep .el-table
      +basicTable
    .noWordBreak
      word-wrap: break-word
    .call-forwarding
      font-size: 14px
      color: $mainColor
    .status
      color: $subFontColor
    .status-danger
      color: $dangerColor
    .icon-info
      color: $intercomBlue

  .el-pagination
    display: flex
    justify-content: flex-start
    margin-top: 28px

.household-info
  max-height: 120px
  overflow: auto
  font-size: 1rem
  p
    white-space: nowrap
    text-overflow: ellipsis
    overflow: hidden
</style>
